import CampoSeletor from "@/components/Formulario/CampoSeletor";
import CampoTexto from "@/components/Formulario/CampoTexto";
import Grid, { GridItem } from "@/components/layout/Grid";
import HBox from "@/components/layout/HBox";
import VBox from "@/components/layout/VBox";
import { mdiApi, mdiConnection, mdiLoading, mdiShapeOutline, mdiWeb } from "@mdi/js";
import Icon from "../../components/Icon";
import axios from "axios";
import React from "react";
import { toast } from "react-toastify";
import { FormIntegracaoApiCustom } from "./FormIntegracaoApiCustom";
import { FormDynamicParams } from "./FormDynamicParams";
import styles from "./FormIntegracaoApi.module.css";
import Card from "../../components/layout/Card";
import Botao from "@/components/Botao";

const TemplateCard: React.FC<{
    template: any;
    setDescription: (description: string) => void;
    onSelect: (key: string) => void;
}> = ({ template, onSelect, setDescription }) => {
    return (
        <Card
            className={styles.templateCard}
            onClick={() => onSelect(template.key)}
            onMouseEnter={() => {
                if (template.display?.description) {
                    setDescription(template.display.description);
                }
            }}
            onMouseLeave={() => {
                setDescription("");
            }}
        >
            <VBox>
                <HBox>
                    <div className={styles.templateIcon}>
                        {template.display?.icon ? (
                            <img src={`/img/integrations/${template.display.icon}-logo.png`} alt={template.display.name || template.key} style={{ height: '1.5rem' }} />
                        ) : (
                            <Icon
                                path={mdiApi}
                                size={1.2}
                            />
                        )}
                    </div>
                    <VBox style={{ flex: 1 }}>
                        <h3 className={styles.templateTitle}>{template.display?.name || template.key}</h3>
                        <small className={styles.templateVersion}>
                            Última versão: {template.latestVersion}
                        </small>
                    </VBox>
                </HBox>
            </VBox>
        </Card>
    );
};

const TemplateSelector: React.FC<{
    availableTemplates: any[];
    onSelect: (key: string) => void;
    selectedFilter: string;
    onFilter: (category: string) => void;
}> = ({ availableTemplates, selectedFilter, onSelect, onFilter }) => {
    const [description, setDescription] = React.useState<string>("");

    const categories = availableTemplates.reduce((acc: Record<string, any[]>, template) => {
        const category = template.display?.category || "outros";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(template);
        return acc;
    }, {});

    const filteredTemplates = selectedFilter === "all"
        ? availableTemplates
        : categories[selectedFilter ?? 'outros'] || [];

    return (
        <VBox gap="16px">
            <h3>Selecione o tipo de integração</h3>

            <Grid style={{ flex: 1, height: '100%' }}>
                <GridItem sm={12} md={2}>
                    <Botao
                        size="sm"
                        variant={selectedFilter === 'all' ? "primary" : "outline-primary"}
                        onClick={() => onFilter("all")}
                        style={{ width: '100%' }}
                    >
                        Todos
                    </Botao>
                    {Object.keys(categories).sort((a, b) => a.localeCompare(b)).map((category) => (
                        <Botao
                            size="sm"
                            key={category}
                            variant={selectedFilter === category ? "info" : "outline-secondary"}
                            onClick={() => onFilter(category)}
                            style={{ width: '100%', marginTop: '8px' }}
                        >
                            {category}
                        </Botao>
                    ))}
                </GridItem>
                <GridItem sm={12} md={10}>
                    <Grid>
                        {filteredTemplates.map((template) => (
                            <GridItem key={template.key} sm={12} md={4} lg={3}>
                                <TemplateCard template={template} onSelect={onSelect} setDescription={setDescription} />
                            </GridItem>
                        ))}

                        <GridItem sm={12} md={4} lg={3}>
                            <Card
                                className={styles.templateCard}
                                onClick={() => onSelect("custom")}
                                onMouseEnter={() => setDescription("Crie uma integração personalizada configurando endpoints e parâmetros manualmente")}
                                onMouseLeave={() => setDescription("")}
                            >
                                <VBox>
                                    <HBox>
                                        <div className={styles.templateIcon}>
                                            <Icon path={mdiConnection} size={1.2} />
                                        </div>
                                        <VBox style={{ flex: 1 }}>
                                            <h3 className={styles.templateTitle}>API Personalizada</h3>
                                        </VBox>
                                    </HBox>
                                </VBox>
                            </Card>
                        </GridItem>
                    </Grid>
                </GridItem>
            </Grid>
            <p className={styles.templateDesc}>
                {description}
            </p>
        </VBox >
    );
};

type Props = {
    value?: any;
    onChange: (value: any) => void;
    fieldErrors?: Record<string, string>;
    availableTemplates: any[];
};

export const FormIntegracaoApi = ({
    value,
    onChange,
    fieldErrors,
    availableTemplates,
}: Props) => {
    const [schema, setSchema] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [filter, setFilter] = React.useState<string>("all");

    const [selectedTemplate, setSelectedTemplate] = React.useState<string | null>(null);


    // Obtem as versões do template selecionado
    const templateVersions = React.useMemo(() => {
        if (!value?.template_key || value?.template_key === "custom") return [];

        const template = availableTemplates.find((t) => t.key === value.template_key);
        return template?.versions || [];
    }, [value?.template_key, availableTemplates]);

    // Busca o schema do template quando muda a seleção
    React.useEffect(() => {
        if (!value?.template_key || value?.template_key === "custom") {
            setSchema([]);
            return;
        }

        // Se não tem versão selecionada e existem versões, seleciona a última
        if (!value?.template_version && templateVersions.length > 0) {
            const latestVersion = templateVersions[templateVersions.length - 1];
            onChange({
                ...value,
                template_version: latestVersion,
            });
        }

        // Busca o schema
        if (value?.template_version) {
            setLoading(true);

            axios
                .get(`/api/integrations/templates/${value.template_key}/${value.template_version}`)
                .then((response) => {
                    if (response.data?.ok && response.data?.result) {
                        setSchema(response.data.result.schema || []);

                        // Atualiza os valores no formulário com valores padrão
                        const defaultValues: Record<string, any> = {};
                        response.data.result.schema.forEach((field: any) => {
                            if (field.defaultValue !== undefined && !value.params?.[field.name]) {
                                defaultValues[field.name] = field.defaultValue;
                            }
                        });

                        if (Object.keys(defaultValues).length > 0) {
                            onChange({
                                ...value,
                                template_key: value.template_key,
                                template_version: value.template_version,
                                params: {
                                    ...value.params,
                                    ...defaultValues,
                                },
                            });
                        } else {
                            onChange({
                                ...value,
                                template_key: value.template_key,
                                template_version: value.template_version,
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error("Erro ao buscar schema:", error);
                    toast.error("Erro ao carregar configurações do template");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [value?.template_key, value?.template_version, templateVersions]);

    const handleTemplateSelect = (key: string) => {
        setSelectedTemplate(key);

        if (key === "custom") {
            onChange({
                name: "API Personalizada",
                template_key: "custom",
                template_version: null,
                params: {
                    endereco_host: "",
                    endpoints: [],
                },
                active: true,
            });
        } else {
            onChange({
                name: availableTemplates.find(t => t.key === key)?.display?.name || key,
                template_key: key,
                template_version: "",
                params: {},
                active: true,
            });
        }
    };

    if (!value?.id && !value?.template_key) {
        return <TemplateSelector
            availableTemplates={availableTemplates}
            onSelect={handleTemplateSelect}
            selectedFilter={filter}
            onFilter={setFilter}
        />
    }

    return (
        <VBox gap="16px">
            <input
                type="text"
                name="username"
                style={{
                    position: 'absolute',
                    height: 0,
                    border: 'none',
                    outline: 'none',
                    opacity: 0,
                }}
            />
            <input
                type="password"
                name="password"
                style={{
                    position: 'absolute',
                    height: 0,
                    border: 'none',
                    outline: 'none',
                    opacity: 0,
                }}
            />
            <Grid>
                <GridItem sm={12} md={6}>
                    <CampoTexto
                        label="Nome da integração"
                        value={value?.name || ""}
                        onChange={(v) => onChange({ ...value, name: v })}
                        required
                        error={fieldErrors?.name}
                    />
                </GridItem>

                <GridItem sm={12} md={3}>
                    <CampoSeletor
                        label="Tipo de integração"
                        value={value?.template_key || "custom"}
                        onChange={(v) => {
                            onChange({
                                ...value,
                                template_key: v || "custom",
                                template_version: null,
                                params: {}
                            });
                        }}
                        options={[
                            { id: "custom", label: "API Personalizada" },
                            ...availableTemplates.map((t) => ({
                                id: t.key,
                                label: t.display?.name || t.key,
                            })),
                        ]}
                        error={fieldErrors?.template_key}
                        disabled={value?.id > 0}
                    />
                </GridItem>

                <GridItem sm={12} md={3}>
                    <CampoSeletor
                        label="Versão"
                        value={value?.template_version}
                        onChange={(v) => onChange({ ...value, template_version: v ?? "1.0" })}
                        options={templateVersions.map((v: any) => ({ id: v, label: `v${v}` }))}
                        error={fieldErrors?.template_version}
                        disabled={value?.template_key === "custom"}
                    />
                </GridItem>
            </Grid>

            {value?.template_key === "custom" && (
                <FormIntegracaoApiCustom
                    value={value?.params}
                    onChange={(v: any) => onChange({ ...value, params: v })}
                    fieldErrors={fieldErrors}
                />
            )}

            {loading ? (
                <HBox style={{ justifyContent: "center", padding: "2rem" }}>
                    <Icon path={mdiLoading} size={1.5} spin />
                    <span style={{ marginLeft: "1rem" }}>Carregando configurações...</span>
                </HBox>
            ) : (
                schema.length > 0 && (
                    <>
                        <h3>Parâmetros de Configuração</h3>
                        <FormDynamicParams
                            schema={schema}
                            values={value?.params || {}}
                            onChange={(params) => onChange({ ...value, params })}
                            errors={fieldErrors}
                        />
                    </>
                )
            )}
        </VBox>
    );
};